import type { Action } from '@unifyapps/defs/types/action';
import { useMemo } from 'react';
import type { OnActionArgs } from '../../context/ActionsProvider/context';
import { useGlobalStateStore } from '../../stores/GlobalStateStore';
import type { RepeatableActions } from '../../utils/repeatables/attachInstances';
import { useControlBlockMethod } from '../useGlobalBlockActions/actions/controlBlockMethod';
import type { ComputeContextMode } from './const';
import type { ComputeContextType } from './context/types';
import { useLatest } from '@unifyapps/hooks/useLatest';

/**
 * Builds the `removeBlocksState` + `callBlockMethod` pair exposed to Repeatable
 * `.instances[N]` proxies. Gated to non-readonly mode so passive expression
 * evaluation can't trigger writes. The caller owns the live-context source
 * (a builder fn or a context ref); we wrap it in a ref-indirection so an
 * inline arrow at the call site doesn't bust the memo and rebuild the actions
 * object — invalidating its identity busts the ContextBuilder cache.
 */
export function useRepeatableActions({
  mode,
  getLiveContext,
  defaultInstanceId,
}: {
  mode: ComputeContextMode;
  getLiveContext: () => ComputeContextType | null | undefined;
  defaultInstanceId?: string | null;
}): RepeatableActions | undefined {
  const globalStateStore = useGlobalStateStore();
  const { onCallBlockMethod } = useControlBlockMethod();

  // we are using useLatest to avoid busting the memo and rebuild the actions object
  // so we can reuse the existing context cache more
  const { ref: getLiveContextRef } = useLatest(getLiveContext);
  const { ref: defaultInstanceIdRef } = useLatest(defaultInstanceId);

  return useMemo<RepeatableActions | undefined>(() => {
    if (mode === 'readonly') return undefined;

    return {
      removeBlocksState: (ids: string[]) =>
        globalStateStore.getState().actions.removeBlocksState(ids),
      callBlockMethod: async ({
        blockId,
        methodName,
        targetBlockId,
        methodPayload,
        instanceId,
      }) => {
        try {
          // resolve a fresh compute context at dispatch time so payload
          // bindings see the live blocks state, not the closure-time snapshot
          const liveContext = getLiveContextRef.current();
          await onCallBlockMethod({
            blockId,
            methodName,
            targetBlockId,
            instanceId: instanceId ?? defaultInstanceIdRef.current ?? undefined,
            action: {
              type: 'TRIGGER_BLOCK_METHOD',
              payload: { methodPayload },
            } as unknown as Action,
            actionContext: (liveContext ?? {}) as OnActionArgs['actionContext'],
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[Repeatable.instances callBlockMethod]', {
            blockId,
            methodName,
            targetBlockId,
            err,
          });
        }
      },
    };
  }, [globalStateStore, mode, onCallBlockMethod, getLiveContextRef, defaultInstanceIdRef]);
}
